/**
 * IssueConfirmation Page
 * Displays confirmation after successful issue submission
 * Shows tracking number and instructions for tracking
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiCopy, FiSearch } from 'react-icons/fi';
import PublicLayout from '../components/layout/PublicLayout';
import apiService from '../services/api';
import { TrackingLookupResponse } from '../types';
import { toast } from '../utils/toast';
import Spinner from '../components/common/Spinner';
import Button from '../components/common/Button';

const IssueConfirmation: React.FC = () => {
  const { trackingNumber } = useParams<{ trackingNumber: string }>();
  const navigate = useNavigate();

  const [issueData, setIssueData] = useState<TrackingLookupResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!trackingNumber) {
      navigate('/');
      return;
    }

    loadIssueData();
  }, [trackingNumber]);

  const loadIssueData = async () => {
    try {
      setLoading(true);
      const data = await apiService.trackIssue(trackingNumber!);
      setIssueData(data);
    } catch (err: any) {
      console.error('Failed to load issue data:', err);
      toast.error('Unable to load issue details');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyTracking = () => {
    if (trackingNumber) {
      navigator.clipboard.writeText(trackingNumber);
      setCopied(true);
      toast.success('Tracking number copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <Spinner size="lg" />
        </div>
      </PublicLayout>
    );
  }

  if (!issueData) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Issue Not Found</h1>
          <p className="text-gray-600 mb-6">
            We couldn't find an issue with this tracking number.
          </p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </PublicLayout>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'acknowledged':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-purple-100 text-purple-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      case 'spam':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 1:
        return 'Critical';
      case 2:
        return 'High';
      case 3:
        return 'Medium';
      case 4:
        return 'Low';
      case 5:
        return 'Very Low';
      default:
        return 'Medium';
    }
  };

  // Extract issue data for easier access and type safety
  const issue = issueData?.issue;

  if (!issue) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Issue Not Found</h1>
          <p className="text-gray-600 mb-6">
            We couldn't find an issue with this tracking number.
          </p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-green-100 p-4">
              <FiCheckCircle className="w-16 h-16 text-green-600" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Issue Reported Successfully
          </h1>
          <p className="text-lg text-gray-600">
            Thank you for reporting this issue. We've received your report and will address it shortly.
          </p>
        </div>

        {/* Tracking Number Card */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Tracking Number</h2>

          <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-3">
              <FiSearch className="w-6 h-6 text-gray-500" />
              <span className="text-2xl font-mono font-bold text-gray-900">
                {trackingNumber}
              </span>
            </div>
            <button
              onClick={handleCopyTracking}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <FiCopy className="w-4 h-4" />
              <span className="text-sm font-medium">
                {copied ? 'Copied!' : 'Copy'}
              </span>
            </button>
          </div>

          <p className="text-sm text-gray-600">
            Save this tracking number to check the status of your report at any time.
            You can use it to track updates and resolution progress.
          </p>
        </div>

        {/* Issue Details Card */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Issue Details</h2>

          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <p className="text-gray-900">{issue.title}</p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <p className="text-gray-900 whitespace-pre-wrap">{issue.description}</p>
            </div>

            {/* Status and Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(issue.status)}`}>
                  {issue.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  {getPriorityLabel(issue.priority)}
                </span>
              </div>
            </div>

            {/* Submitted Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Submitted
              </label>
              <p className="text-gray-900">
                {new Date(issue.created_at).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* What Happens Next */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-blue-900 mb-4">What Happens Next?</h2>
          <ul className="space-y-3 text-blue-800">
            <li className="flex items-start">
              <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-200 text-blue-900 font-semibold text-sm mr-3">
                1
              </span>
              <span>
                Our team will review your report and assign it to the appropriate personnel.
              </span>
            </li>
            <li className="flex items-start">
              <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-200 text-blue-900 font-semibold text-sm mr-3">
                2
              </span>
              <span>
                {issue.priority === 1
                  ? 'As a safety hazard, this will be addressed immediately with high priority.'
                  : 'The issue will be addressed based on its priority level.'}
              </span>
            </li>
            <li className="flex items-start">
              <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-200 text-blue-900 font-semibold text-sm mr-3">
                3
              </span>
              <span>
                {issue.reporter_email
                  ? 'We will send updates to your email address as the issue progresses.'
                  : 'Use your tracking number to check for updates on this page.'}
              </span>
            </li>
            <li className="flex items-start">
              <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-200 text-blue-900 font-semibold text-sm mr-3">
                4
              </span>
              <span>
                You'll be notified when the issue is resolved.
              </span>
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate('/')}
            variant="secondary"
          >
            Go Home
          </Button>
          <Button
            onClick={handleCopyTracking}
            variant="primary"
          >
            <FiCopy className="w-4 h-4 mr-2" />
            Copy Tracking Number
          </Button>
        </div>
      </div>
    </PublicLayout>
  );
};

export default IssueConfirmation;
